import { Mark } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

/** Safety cap for `exec` loops per text node (pathological input should not hang the editor). */
export const MAX_BOOLEAN_HIGHLIGHT_REGEX_ITERATIONS_PER_TEXT_NODE = 50_000;

/** DOM spec for quoted-term marks; uses a content hole so copy/paste does not duplicate text. */
export const quotedBooleanHighlightDomSpec = [
  "span",
  { class: "boolean-quoted quoted-text" },
  0,
] as const;

const keywordVariantClass = (keyword: string): string => {
  if (keyword === "NOT") return "boolean-keyword--not";
  if (keyword === "AND") return "boolean-keyword--and";
  if (keyword === "OR") return "boolean-keyword--or";
  return "boolean-keyword--other";
};

const BooleanHighlight = Mark.create({
  name: "booleanHighlight",

  addAttributes() {
    return {
      keyword: {
        default: null,
      },
      quotedText: {
        default: null,
      },
      /** Proximity / boost slice, e.g. `~15` (tilde + digits). */
      tildeBoost: {
        default: null,
      },
      /** Identifier + `:`, e.g. `headline:` or `NEAR:` (dark theme highlights orange). */
      colonOperator: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span",
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false;

          if (
            element.classList.contains("boolean-quoted") ||
            element.classList.contains("quoted-text")
          ) {
            const raw = element.textContent ?? "";
            const inner =
              (raw.startsWith('"') && raw.endsWith('"')) ||
              (raw.startsWith("'") && raw.endsWith("'"))
                ? raw.slice(1, -1)
                : raw;
            return { quotedText: inner };
          }

          if (element.classList.contains("boolean-proximity")) {
            const v = element.getAttribute("data-proximity");
            if (v?.startsWith("~")) return { tildeBoost: v };
            return false;
          }

          if (element.classList.contains("boolean-colon-operator")) {
            const v = element.getAttribute("data-colon-operator");
            if (v && /^[A-Za-z_][A-Za-z0-9_]+:$/.test(v))
              return { colonOperator: v };
            return false;
          }

          if (!element.classList.contains("boolean-keyword")) return false;

          const dataKw = element.getAttribute("data-keyword");
          if (dataKw === "AND" || dataKw === "OR" || dataKw === "NOT") {
            return { keyword: dataKw };
          }
          if (element.classList.contains("boolean-keyword--not")) {
            return { keyword: "NOT" };
          }
          if (element.classList.contains("boolean-keyword--and")) {
            return { keyword: "AND" };
          }
          if (element.classList.contains("boolean-keyword--or")) {
            return { keyword: "OR" };
          }
          if (element.classList.contains("text-red-600"))
            return { keyword: "NOT" };
          if (element.classList.contains("text-blue-600"))
            return { keyword: "AND" };
          if (element.classList.contains("text-green-600"))
            return { keyword: "OR" };
          return false;
        },
      },
    ];
  },

  renderHTML({ mark }) {
    if (mark.attrs.keyword) {
      const keyword = mark.attrs.keyword as string;
      return [
        "span",
        {
          class: `boolean-keyword ${keywordVariantClass(keyword)}`,
          "data-keyword": keyword,
        },
      ];
    }
    if (mark.attrs.colonOperator) {
      const slice = mark.attrs.colonOperator as string;
      return [
        "span",
        {
          class: "boolean-colon-operator",
          "data-colon-operator": slice,
        },
      ];
    }
    if (mark.attrs.tildeBoost) {
      const slice = mark.attrs.tildeBoost as string;
      return [
        "span",
        {
          class: "boolean-proximity",
          "data-proximity": slice,
        },
      ];
    }
    if (mark.attrs.quotedText) {
      return [...quotedBooleanHighlightDomSpec];
    }
    return ["span", {}];
  },

  addProseMirrorPlugins() {
    let timeoutId: number | null = null;
    // Separate timer for the focus handler so a focus event does not cancel a
    // pending 500ms highlight triggered by typing, paste, or key events.
    let focusTimeoutId: number | null = null;
    let isDispatching = false;

    const processHighlights = (view: EditorView) => {
      if (isDispatching) return;

      const { state, dispatch } = view;
      const { doc } = state;

      const tr = state.tr;
      tr.removeMark(0, doc.content.size, state.schema.marks.booleanHighlight);

      doc.descendants((node, pos) => {
        if (!node.isText || !node.text) return;

        // Fresh `/g` regex per text node so `lastIndex` never carries across `descendants` callbacks.
        // `word:` (case-insensitive identifier); skip `http://` / `https://`. AND/OR/NOT use the first branch.
        const regex =
          /\b(AND|OR|NOT)\b|("[^"]*")|('[^']*')|(~\d+)|(\b[A-Za-z_][A-Za-z0-9_]+:(?!\/\/))/g;
        let iterations = 0;

        let match: RegExpExecArray | null;
        while (
          iterations < MAX_BOOLEAN_HIGHLIGHT_REGEX_ITERATIONS_PER_TEXT_NODE
        ) {
          match = regex.exec(node.text);
          iterations += 1;
          if (match === null) break;

          const start = pos + (match.index ?? 0);
          const end = start + (match[0]?.length ?? 0);

          const mark = state.schema.marks.booleanHighlight;
          if (!mark) return;

          if (match[1]) {
            tr.addMark(start, end, mark.create({ keyword: match[1] }));
          } else if (match[2] || match[3]) {
            const quotedContent = (match[2] || match[3])?.slice(1, -1) ?? "";
            tr.addMark(start, end, mark.create({ quotedText: quotedContent }));
          } else if (match[4]) {
            tr.addMark(start, end, mark.create({ tildeBoost: match[4] }));
          } else if (match[5]) {
            tr.addMark(start, end, mark.create({ colonOperator: match[5] }));
          }
        }
      });

      if (tr.docChanged) {
        isDispatching = true;
        try {
          dispatch(tr);
        } finally {
          isDispatching = false;
        }
      }
    };

    const debouncedProcess = (view: EditorView) => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      timeoutId = window.setTimeout(() => {
        processHighlights(view);
        timeoutId = null;
      }, 500); // 500ms delay
    };

    return [
      new Plugin({
        props: {
          handleTextInput(view: EditorView) {
            debouncedProcess(view);
            return false;
          },
          handleKeyDown(view: EditorView, event: KeyboardEvent) {
            if (
              event.key === "Backspace" ||
              event.key === "Delete" ||
              event.key === "Enter" ||
              event.key === '"' ||
              event.key === "'" ||
              event.key === "~" ||
              event.key === ":" ||
              (event.key.length === 1 && event.key >= "0" && event.key <= "9")
            ) {
              debouncedProcess(view);
            }
            return false;
          },
          handlePaste(view: EditorView) {
            debouncedProcess(view);
            return false;
          },
          handleDOMEvents: {
            focus(view: EditorView) {
              // Debounce the focus handler — calling processHighlights
              // synchronously here triggers dispatch → onUpdate → setContent →
              // re-focus, creating an infinite loop on every keystroke.
              // Uses a separate timer so a focus event does not cancel a
              // pending 500ms highlight triggered by typing or paste.
              if (focusTimeoutId !== null) {
                clearTimeout(focusTimeoutId);
              }
              focusTimeoutId = window.setTimeout(() => {
                processHighlights(view);
                focusTimeoutId = null;
              }, 200);
              return false;
            },
          },
        },
      }),
    ];
  },
});

export default BooleanHighlight;
